// Homepage "Sample dishes" strip — showcases the visual variety of the
// Meal Plans 2.0 catalogue by rendering the first day's slots resolved
// from a representative plan/area combination. Only rendered when MP2 is
// visible (LIVE for all, PREVIEW for public, TEST for admins). Falls
// back to nothing when the menu cannot be resolved yet.
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Badge } from "@/components/ui/badge";
import { usePublicMp2Catalog } from "@/hooks/usePublicMp2Catalog";
import {
  getPublicUpcomingMenu,
  type UpcomingMenu,
} from "@/lib/public/meal-plans-v2-menu.functions";

const SLOT_ORDER = ["breakfast", "lunch", "snacks", "dinner"];

function tomorrowIso() {
  return new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 10);
}

export function SampleDishes() {
  const { catalog, isMp2Visible } = usePublicMp2Catalog();
  const fn = useServerFn(getPublicUpcomingMenu);

  // Choose a representative plan + first mapped rule/area/pref/combo.
  const seed = useMemo(() => {
    if (!catalog || !catalog.plans || catalog.plans.length === 0) return null;
    const plan = catalog.plans.find((p) => !p.health_review_required) ?? catalog.plans[0];
    const combo =
      catalog.combinations.find((c) =>
        !plan.allowed_combination_ids ||
        plan.allowed_combination_ids.length === 0 ||
        plan.allowed_combination_ids.includes(c.id),
      ) ?? catalog.combinations[0];
    const pref =
      catalog.food_preferences.find((p) =>
        !plan.allowed_preference_codes ||
        plan.allowed_preference_codes.length === 0 ||
        plan.allowed_preference_codes.includes(p.code),
      ) ?? catalog.food_preferences[0];
    const rule =
      catalog.delivery_rules.find((r) =>
        !plan.allowed_delivery_rule_ids ||
        plan.allowed_delivery_rule_ids.length === 0 ||
        plan.allowed_delivery_rule_ids.includes(r.id),
      ) ?? catalog.delivery_rules[0];
    const areaIds = new Set(
      catalog.rule_areas.filter((ra) => ra.rule_id === rule?.id).map((ra) => ra.area_id),
    );
    const area = catalog.delivery_areas.find((a) => areaIds.has(a.id));
    if (!plan || !combo || !pref || !rule || !area) return null;
    return { plan, combo, pref, rule, area };
  }, [catalog]);

  const q = useQuery({
    queryKey: ["mp2-sample-menu", seed?.plan.id ?? ""],
    enabled: Boolean(seed),
    queryFn: () =>
      fn({
        data: {
          plan_id: seed!.plan.id,
          combination_id: seed!.combo.id,
          food_preference_code: seed!.pref.code,
          delivery_rule_id: seed!.rule.id,
          delivery_area_id: seed!.area.id,
          start_date: tomorrowIso(),
          days: 1,
        },
      }),
  });

  if (!isMp2Visible || !seed || q.isLoading) return null;
  const menuData = q.data;
  if (!menuData || (typeof menuData === 'object' && 'error' in menuData)) return null;
  const menu = menuData as UpcomingMenu;
  const day = menu.days?.[0];
  if (!day) return null;

  const dishes = SLOT_ORDER.map((slot) => day.slots[slot]?.primary).filter(
    (d): d is NonNullable<typeof d> => Boolean(d?.image_url),
  );
  if (dishes.length === 0) return null;

  return (
    <Section>
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-2xl">
          <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Sample dishes
          </span>
          <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">
            A taste of tomorrow's kitchen.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Representative of what customers on the {seed.plan.title} plan
            will receive. Actual menus vary by neighbourhood and preference.
          </p>
        </div>
        <Link
          to="/meal-plans"
          className="text-sm font-medium text-primary hover:underline"
        >
          Browse all plans →
        </Link>
      </div>
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {dishes.map((d) => (
          <article
            key={d.id}
            className="overflow-hidden rounded-2xl border border-border/70 bg-card"
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={d.image_url!}
                alt={d.name}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {d.meal_slot}
              </div>
              <div className="mt-1 line-clamp-2 font-display text-lg font-medium">
                {d.name}
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {d.is_veg ? (
                  <Badge variant="secondary" className="text-[10px]">Veg</Badge>
                ) : d.is_veg === false ? (
                  <Badge variant="outline" className="text-[10px]">Non-veg</Badge>
                ) : null}
                {d.calories_kcal != null ? (
                  <Badge variant="outline" className="text-[10px]">
                    {Math.round(Number(d.calories_kcal))} kcal
                  </Badge>
                ) : null}
              </div>
            </div>
          </article>
        ))}
      </div>
    </Section>
  );
}